import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag,
  Award,
  Leaf,
  Shield,
  Truck,
  Sparkles,
  Building2,
  CheckCircle2,
} from 'lucide-react';
import hero from "../assets/hero.png";

const trustStats = [
  { label: 'Freshly Packed Batches', value: 'Regular' },
  { label: 'Direct Farm Sourcing', value: 'Trusted' },
  { label: 'Free Shipping Threshold', value: '₹599+' },
  { label: 'Secure Checkout', value: 'Enabled' },
];

const qualityPillars = [
  {
    icon: Award,
    title: '100% Pure Spices',
    description: 'No adulteration, no artificial color. Serious quality for serious cooking.',
  },
  {
    icon: Leaf,
    title: 'Farm-Sourced Lots',
    description: 'Carefully sourced lots selected for aroma, color, and consistency.',
  },
  {
    icon: Shield,
    title: 'Lab Verified Safety',
    description: 'Every batch follows strict hygiene and safety checks before packing.',
  },
];

const processSteps = [
  {
    icon: Sparkles,
    title: 'Source with Precision',
    detail: 'We shortlist trusted farms and inspect crop quality by season.',
  },
  {
    icon: CheckCircle2,
    title: 'Clean and Test',
    detail: 'Batches are cleaned, processed, and verified for purity and safety.',
  },
  {
    icon: Truck,
    title: 'Pack and Deliver Fresh',
    detail: 'Moisture-safe packaging keeps aroma locked till it reaches your kitchen.',
  },
];

const Home = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-amber-300/20 rounded-full blur-3xl" />
          <div className="absolute top-32 -right-16 w-72 h-72 bg-orange-300/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="space-y-7">
              <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-900 px-4 py-1.5 rounded-full text-sm font-medium border border-amber-300">
                <Sparkles className="h-4 w-4" />
                Premium Spice Standard
              </span>

              <h1 className="text-5xl lg:text-6xl font-bold text-amber-950 leading-tight tracking-tight">
                Bring Home the True Taste of{' '}
                <span className="bg-gradient-to-r from-amber-700 to-orange-600 bg-clip-text text-transparent">
                  Indian Spices
                </span>
              </h1>

              <p className="text-lg text-amber-800 leading-relaxed max-w-xl">
                Handpicked, naturally processed, and quality-verified spices crafted to deliver authentic flavor in every meal.
              </p>

              <div className="flex flex-wrap gap-3">
                <Link to="/shop">
                  <button className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-4 rounded-xl font-semibold text-base shadow-lg transition-all duration-300 inline-flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5" />
                    Explore Products
                  </button>
                </Link>
                <Link to="/about">
                  <button className="bg-white border border-amber-300 hover:border-amber-500 text-amber-900 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300">
                    Know Our Quality
                  </button>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                {trustStats.map((stat) => (
                  <div key={stat.label} className="bg-white/90 border border-amber-200 rounded-xl p-3">
                    <p className="text-lg font-bold text-amber-950 leading-none">{stat.value}</p>
                    <p className="text-xs text-amber-700 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-amber-300/30 via-orange-200/20 to-transparent rounded-[28px] blur-2xl" />
              <div className="relative rounded-[26px] overflow-hidden shadow-[0_20px_55px_rgba(120,53,15,0.14)] bg-transparent">
                <img
                  src={hero}
                  alt="Premium spices assortment"
                  className="w-full h-[360px] sm:h-[430px] lg:h-[520px] object-contain"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full border border-amber-300/70 bg-amber-100/70 blur-[1px]" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white/80 border-y border-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="bg-amber-100 text-amber-900 px-4 py-1.5 rounded-full text-sm font-medium border border-amber-300 inline-block mb-4">
              Quality Promise
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-950">Why Choose GWAL Spices?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {qualityPillars.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <div key={pillar.title} className="group border border-amber-200 rounded-2xl p-7 hover:shadow-xl transition-all duration-300 bg-white">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center mb-5 shadow-md">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-amber-950 mb-2">{pillar.title}</h3>
                  <p className="text-amber-700 leading-relaxed text-sm">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white rounded-2xl border border-amber-200 p-8 shadow-sm">
              <h3 className="text-3xl font-bold text-amber-950 mb-4">From Farm to Flavor</h3>
              <p className="text-amber-800 mb-6">
                Our workflow is designed for consistent taste, safety, and freshness at scale.
              </p>
              <div className="space-y-5">
                {processSteps.map((step, idx) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.title} className="flex gap-4">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-amber-950">{idx + 1}. {step.title}</p>
                        <p className="text-sm text-amber-700 mt-1">{step.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-900 to-orange-900 rounded-2xl p-8 text-white border border-amber-700 shadow-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-800/70 text-amber-100 text-xs font-semibold mb-4">
                <Building2 className="h-4 w-4" />
                B2B and Home Kitchens
              </div>
              <h3 className="text-3xl font-bold leading-tight mb-4">Built for Retail, Restaurants, and Daily Cooking</h3>
              <p className="text-amber-100/90 leading-relaxed mb-6">
                Whether you are a home cook or a business buyer, we maintain product consistency and dependable supply with professional packaging.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="bg-white/10 rounded-lg p-3 border border-white/15">Batch-level quality control</div>
                <div className="bg-white/10 rounded-lg p-3 border border-white/15">Reliable reorder cycle</div>
                <div className="bg-white/10 rounded-lg p-3 border border-white/15">Clean-label confidence</div>
                <div className="bg-white/10 rounded-lg p-3 border border-white/15">Support for bulk queries</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-amber-900 to-orange-900 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5">
            Experience Freshness Like Never Before
          </h2>
          <p className="text-lg text-amber-100 mb-10">
            Order premium spices directly and elevate every dish with authentic flavor.
          </p>
          <Link to="/shop">
            <button className="bg-white hover:bg-amber-50 text-amber-900 px-10 py-4 rounded-xl font-bold text-base shadow-xl transition-all duration-300 inline-flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Browse Products
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
